/**
 * SeancePage — Détail et édition d'une séance de consultation
 *
 * Éditeur TipTap pour les notes + encadré automatique des aspects actifs.
 */

import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';

const RichEditor = lazy(() => import('../components/editor/RichEditor.js').then((m) => ({ default: m.RichEditor })));

interface SeanceData {
  id: number;
  date: string;
  notes: string | null;
  aspectsSnapshot: unknown;
  createdAt: string;
  updatedAt: string;
}

interface TransitAspect {
  date: string;
  planeteTransit: string;
  aspect: string;
  pointNatal: string;
  direction: string;
}

interface TransitsData {
  aspects: TransitAspect[];
}

const ASPECT_COLORS: Record<string, { bg: string; text: string }> = {
  Conjonction: { bg: '#fefce8', text: '#854d0e' },
  Opposition: { bg: '#fef2f2', text: '#991b1b' },
  'Carré': { bg: '#fef2f2', text: '#991b1b' },
  Trigone: { bg: '#f0fdf4', text: '#166534' },
  Sextile: { bg: '#f0fdf4', text: '#166534' },
};

function formatDateInput(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toISOString().split('T')[0]!;
  } catch {
    return '';
  }
}

function formatDateFR(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function SeancePage() {
  const { id, seanceId } = useParams<{ id: string; seanceId: string }>();
  const isNew = seanceId === 'nouveau';

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]!);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');

  // Aspects actifs autour de la date
  const [activeTransits, setActiveTransits] = useState<TransitAspect[]>([]);

  // Charger la séance existante
  useEffect(() => {
    if (isNew || !seanceId) return;

    apiFetch<SeanceData>(`/clients/${id}/seances/${seanceId}`)
      .then((res) => {
        if (res.success && res.data) {
          setDate(formatDateInput(res.data.date));
          setNotes(res.data.notes ?? '');
        } else {
          setError(res.error ?? 'Séance introuvable');
        }
      })
      .finally(() => setLoading(false));
  }, [id, seanceId, isNew]);

  // Charger les transits actifs autour de la date de séance
  useEffect(() => {
    if (!id || !date) return;

    // Trouver le chart transits le plus récent
    apiFetch<{ charts: Array<{ id: number; type: string; createdAt: string }> }>(`/clients/${id}`)
      .then((res) => {
        if (!res.success || !res.data) return;
        const transitChart = res.data.charts
          ?.filter((c: { type: string }) => c.type === 'TRANSITS')
          .sort((a: { createdAt: string }, b: { createdAt: string }) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        if (!transitChart) return;

        const d = new Date(date);
        const debut = new Date(d); debut.setDate(debut.getDate() - 7);
        const fin = new Date(d); fin.setDate(fin.getDate() + 7);

        const params = new URLSearchParams({
          dateDebut: formatDateFR(debut),
          dateFin: formatDateFR(fin),
          exclureLune: 'true',
        });

        return apiFetch<TransitsData>(`/clients/${id}/charts/${transitChart.id}/transits?${params}`);
      })
      .then((res) => {
        if (res?.success && res.data) {
          setActiveTransits(res.data.aspects);
        }
      });
  }, [id, date]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaved(false);
    setError('');

    try {
      if (isNew) {
        const res = await apiFetch<SeanceData>(`/clients/${id}/seances`, {
          method: 'POST',
          body: JSON.stringify({ date, notes }),
        });
        if (res.success && res.data) {
          // Rediriger vers la séance créée
          window.history.replaceState(null, '', `/clients/${id}/seances/${res.data.id}`);
          setSaved(true);
        } else {
          setError(res.error ?? 'Erreur lors de la création');
        }
      } else {
        const res = await apiFetch(`/clients/${id}/seances/${seanceId}`, {
          method: 'PATCH',
          body: JSON.stringify({ date, notes }),
        });
        if (res.success) {
          setSaved(true);
        } else {
          setError(res.error ?? 'Erreur lors de la sauvegarde');
        }
      }
    } catch {
      setError('Erreur de connexion');
    } finally {
      setSaving(false);
      setTimeout(() => setSaved(false), 3000);
    }
  }, [id, seanceId, isNew, date, notes]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
        Chargement...
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.4rem', fontWeight: 700, color: colors.text }}>
        <i className="bi bi-journal-richtext" style={{ color: '#0891b2', marginRight: 8 }} />
        {isNew ? 'Nouvelle séance' : 'Séance'}
      </h1>

      {/* Date */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textMid, marginBottom: 4 }}>
          Date de la séance
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 6, border: `1px solid ${colors.border}`,
            fontSize: '0.9rem', fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Aspects actifs ce jour */}
      {activeTransits.length > 0 && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.8rem',
        }}>
          <div style={{ fontWeight: 600, color: '#0c4a6e', marginBottom: '0.5rem' }}>
            <i className="bi bi-arrow-repeat" style={{ marginRight: 6 }} />
            Transits actifs ±7 jours ({activeTransits.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
            {activeTransits.slice(0, 20).map((a, i) => {
              const ac = ASPECT_COLORS[a.aspect] ?? { bg: '#f5f5f5', text: '#666' };
              return (
                <span key={i} style={{
                  background: ac.bg, color: ac.text,
                  padding: '2px 6px', borderRadius: 4, fontSize: '0.7rem',
                }}>
                  {a.planeteTransit} {a.aspect} {a.pointNatal}
                </span>
              );
            })}
            {activeTransits.length > 20 && (
              <span style={{ fontSize: '0.7rem', color: colors.textMuted }}>
                +{activeTransits.length - 20} autres
              </span>
            )}
          </div>
        </div>
      )}

      {/* Éditeur de notes */}
      <div style={{
        background: '#fff', borderRadius: 8, padding: '1rem',
        marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        minHeight: 300,
      }}>
        <Suspense fallback={<div style={{ padding: '2rem', color: colors.textMuted, textAlign: 'center' }}>Chargement de l'éditeur...</div>}>
          <RichEditor
            value={notes}
            onChange={setNotes}
            placeholder="Notes de séance..."
          />
        </Suspense>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 24px', borderRadius: 6, border: 'none',
            background: '#0891b2', color: '#fff', fontSize: '0.9rem',
            fontWeight: 500, cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.7 : 1, fontFamily: 'inherit',
          }}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        {saved && (
          <span style={{ color: '#166534', fontSize: '0.85rem' }}>
            <i className="bi bi-check-circle" style={{ marginRight: 4 }} />
            Enregistré
          </span>
        )}
        {error && (
          <span style={{ color: '#991b1b', fontSize: '0.85rem' }}>
            <i className="bi bi-exclamation-circle" style={{ marginRight: 4 }} />
            {error}
          </span>
        )}
      </div>
    </div>
  );
}
